import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SimilarJob {
  id: string;
  position: string;
  company: string;
  location: string;
  salary: string;
  image: string;
}

interface JobSidebarProps {
  salary: string;
  deadline: string;
  views: number;
  applicants: number;
  location: string;
  similarJobs: SimilarJob[];
  onApply?: () => void;
}

export default function JobSidebar({ salary, deadline, views, applicants, location, similarJobs, onApply }: JobSidebarProps) {
  const [shared, setShared] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const navigate = useNavigate();

  const daysLeft = () => {
    const diff = new Date(deadline).getTime() - new Date().getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  return (
    <div className="w-[360px] flex-shrink-0">
      <div className="sticky top-28 space-y-6">
        {/* Apply Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white/5 backdrop-blur-md border border-neon-purple/20 rounded-2xl p-7"
        >
          {/* Salary */}
          <div className="mb-6">
            <p className="text-xs text-gray-500 mb-1">급여</p>
            <p className="text-3xl font-black text-emerald-400">{salary}</p>
          </div>

          {/* Deadline */}
          <div className="flex items-center justify-between bg-dark-deep/60 rounded-xl px-5 py-4 mb-6">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">마감까지</p>
              <p className="text-2xl font-bold text-neon-pink">{daysLeft()}<span className="text-sm text-gray-400 ml-1">일</span></p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-0.5">마감일</p>
              <p className="text-sm text-gray-300">{deadline}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 text-center bg-dark-deep/40 rounded-xl py-3">
              <p className="text-lg font-bold text-white">{views.toLocaleString()}</p>
              <p className="text-xs text-gray-500">조회수</p>
            </div>
            <div className="flex-1 text-center bg-dark-deep/40 rounded-xl py-3">
              <p className="text-lg font-bold text-white">{applicants}</p>
              <p className="text-xs text-gray-500">지원자</p>
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleApply}
            className="w-full h-14 rounded-xl font-bold text-base transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 bg-gradient-neon text-white shadow-neon-purple hover:shadow-[0_0_30px_rgba(168,85,247,0.6)]"
          >
            지금 지원하기
            <i className="ri-arrow-right-line text-lg"></i>
          </button>

          {/* Sub Actions */}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => setBookmarked(!bookmarked)}
              className={`flex-1 h-11 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 border ${
                bookmarked
                  ? 'border-neon-purple text-neon-purple bg-neon-purple/10'
                  : 'border-white/15 text-gray-400 hover:border-neon-purple hover:text-neon-purple'
              }`}
            >
              <i className={`${bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-base`}></i>
              스크랩
            </button>
            <button
              onClick={handleShare}
              className={`flex-1 h-11 rounded-xl text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 border ${
                shared
                  ? 'border-emerald-400 text-emerald-400 bg-emerald-400/10'
                  : 'border-white/15 text-gray-400 hover:border-neon-pink hover:text-neon-pink'
              }`}
            >
              <i className={`${shared ? 'ri-check-line' : 'ri-share-line'} text-base`}></i>
              {shared ? '복사됨' : '공유하기'}
            </button>
          </div>
        </motion.div>

        {/* Location Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden"
        >
          <div className="w-full h-44">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3165.354!2d127.0276!3d37.4979!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357ca15aaaaaaaab%3A0x1234567890abcdef!2z6rCV64Ko!5e0!3m2!1sko!2skr!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)' }}
              allowFullScreen
              loading="lazy"
              title="근무지 위치"
            ></iframe>
          </div>
          <div className="p-5">
            <p className="text-sm text-gray-300 flex items-start gap-2">
              <i className="ri-map-pin-line text-neon-pink mt-0.5 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
              {location}
            </p>
          </div>
        </motion.div>

        {/* Similar Jobs */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6"
        >
          <h4 className="text-sm font-bold text-neon-purple mb-4">유사한 포지션</h4>
          <div className="space-y-4">
            {similarJobs.map((job) => (
              <a
                key={job.id}
                href={`/job/${job.id}`}
                onClick={(e) => { e.preventDefault(); navigate(`/job/${job.id}`); window.scrollTo(0, 0); }}
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-white/10 group-hover:border-neon-purple/40 transition-colors">
                  <img src={job.image} alt={job.position} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white group-hover:text-neon-purple transition-colors truncate">{job.position}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{job.company} · {job.location}</p>
                  <p className="text-xs font-semibold text-emerald-400 mt-1">{job.salary}</p>
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
